import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormControlStatus,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';

import { TranslocoService } from '@jsverse/transloco';
import { MarkdownModule } from 'ngx-markdown';
import { Subscription } from 'rxjs';

import { v4 as UUIDv4 } from 'uuid';
import { ErrorObject } from '@inmoment/the-kitchen';

import { AppSharedModule } from 'app/app-shared.module';
import { ItemLabelsComponent } from 'app/components/item-labels/item-labels.component';
import { AiInsight } from 'app/providers/semantria-api/ai-insights.service';
import { ItemLabel, ObjectLabel } from 'app/providers/storage-api/object-label.service';
import { noWhitespaceValidator } from 'app/utils/customValidators';
import { ErrorObjectUtils } from 'app/utils/error-object.utils';
import { WidgetAiInsightsService } from '../../services/widget-ai-insights.service';

interface PublishInsightForm {
  question: FormControl<string>;
  insight: FormControl<string>;
  includeImage: FormControl<boolean>;
  comment: FormControl<string>;
}
@Component({
  selector: 'app-publish-insight-dialog',
  imports: [AppSharedModule, MarkdownModule, ItemLabelsComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './publish-insight-dialog.component.html',
  styleUrl: './publish-insight-dialog.component.scss',
})
export class PublishInsightDialogComponent implements OnInit, OnDestroy {
  insight = input<AiInsight>();
  imageExists = input<boolean>(false);
  isImageLoading = input<boolean>(false);
  insightImage = input<string | ArrayBuffer | undefined>(undefined);
  modalClosed = output<{ insight: AiInsight; labels: Partial<ItemLabel>[] } | null>();
  publishInsightForm: FormGroup;

  questionStatus: ErrorObject;
  insightStatus: ErrorObject;
  commentStatus: ErrorObject;
  insightLabels: Partial<ItemLabel>[] = [];
  searchLabels: ObjectLabel[] = [];

  private readonly fb = inject(FormBuilder);
  private readonly translocoService = inject(TranslocoService);
  widgetAiInsightsService = inject(WidgetAiInsightsService);
  private tagsSubscription?: Subscription;

  ngOnInit(): void {
    this.publishInsightForm = this.fb.group<PublishInsightForm>({
      question: new FormControl(this.insight().properties.shortPurpose, [
        Validators.required,
        noWhitespaceValidator,
      ]),
      insight: new FormControl(this.insight().localizations?.[0]?.text ?? '', [
        Validators.required,
        noWhitespaceValidator,
      ]),
      includeImage: new FormControl(!!this.insight().properties.includeImage),
      comment: new FormControl(this.insight().comment ?? '', [Validators.maxLength(750)]),
    });

    this.publishInsightForm.statusChanges.subscribe((status) =>
      this.createErrorObjectAndValidateInput(status)
    );
    this.insightLabels =
      this.insight().tags?.map((tag) => {
        try {
          const parsed = JSON.parse(tag);
          return { id: parsed.id || tag, label: parsed.label || tag };
        } catch {
          return { id: tag, label: tag };
        }
      }) || [];

    this.tagsSubscription = this.widgetAiInsightsService.aiInsightTags$.subscribe((tags) => {
      const timestamp = new Date().toISOString();
      this.searchLabels = (tags ?? [])
        .map((tag) => tag?.trim())
        .filter((tag): tag is string => !!tag)
        .map((tag) => ({
          id: tag,
          label: tag,
          groupId: '',
          createdAt: timestamp,
          modifiedAt: timestamp,
        }));
    });
  }

  ngOnDestroy(): void {
    this.tagsSubscription?.unsubscribe();
  }

  onLabelApplied(newLabel: Partial<ItemLabel>) {
    if (!newLabel.id) {
      newLabel.id = UUIDv4();
    }
    this.insightLabels = [...this.insightLabels, newLabel];
  }

  onLabelRemoved($event: string) {
    this.insightLabels = this.insightLabels.filter((l) => l.id !== $event);
  }

  onLabelUpdated($event: Partial<ItemLabel>) {
    const labelIdx = this.insightLabels.findIndex((l) => l.id === $event.id);
    this.insightLabels.splice(labelIdx, 1, $event);
    this.insightLabels = [...this.insightLabels];
  }

  onPublish() {
    this.insight().properties.shortPurpose = this.publishInsightForm.value.question;
    this.insight().localizations[0].text = this.publishInsightForm.value.insight;
    this.insight().comment = this.publishInsightForm.value.comment;
    this.publishInsightForm.value.includeImage
      ? (this.insight().properties.includeImage = true)
      : (this.insight().properties.includeImage = false);
    this.modalClosed.emit({ insight: this.insight(), labels: this.insightLabels });
  }

  onCancel() {
    this.modalClosed.emit(null);
  }

  private createErrorObjectAndValidateInput(status: FormControlStatus): void {
    this.questionStatus = ErrorObjectUtils.create();
    this.insightStatus = ErrorObjectUtils.create();
    this.commentStatus = ErrorObjectUtils.create();
    const questionError = this.publishInsightForm.controls.question.errors;
    const insightError = this.publishInsightForm.controls.insight.errors;
    const commentError = this.publishInsightForm.controls.comment.errors;
    this.validateInput(status, questionError, this.questionStatus);
    this.validateInput(status, insightError, this.insightStatus);
    this.validateInput(status, commentError, this.commentStatus);
  }

  private validateInput(formStatus: string, errors: ValidationErrors, errorObj: ErrorObject) {
    if (formStatus === 'INVALID' && errors) {
      switch (Object.keys(errors)[0]) {
        case 'required':
        case 'whitespace':
          ErrorObjectUtils.setError(
            errorObj,
            this.translocoService.translate('widgets.publish-insight-dialog.required')
          );
          break;
        case 'maxlength':
          ErrorObjectUtils.setError(
            errorObj,
            this.translocoService.translate('widgets.publish-insight-dialog.char-limit-reached', {
              maxlength: errors.maxlength.requiredLength,
            })
          );
      }
    } else {
      ErrorObjectUtils.setNoError(errorObj);
    }
  }
}
